import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, DatePicker } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Info } from "lucide-react";

const CODES = ["STANDARD", "REDUCED", "ZERO_RATE", "EXEMPT_INTRACOMMUNITY", "EXEMPT_EXPORT", "EXEMPT_OTHER"];

export default function TaxRatesTab({ isAdmin, settings, onSettings }) {
  const { t, fmtDate } = useI18n();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "STANDARD", rate: "", valid_from: "" });
  const [impact, setImpact] = useState(null);

  const load = useCallback(() => api.get("/tax-rates").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open || !f.valid_from || f.rate === "") { setImpact(null); return; }
    api.get(`/tax-rates/impact?code=${f.code}&rate=${Number(f.rate) || 0}&valid_from=${f.valid_from}`)
      .then((r) => setImpact(r.data)).catch(() => setImpact(null));
  }, [open, f.code, f.rate, f.valid_from]);

  const save = async () => {
    try {
      await api.post("/tax-rates", { code: f.code, rate: Number(f.rate) || 0, valid_from: f.valid_from });
      toast.success(t("tax.created")); setOpen(false); setF({ code: "STANDARD", rate: "", valid_from: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(t("tax.delConfirm"))) return;
    try { await api.delete(`/tax-rates/${r.id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setAuthority = async (v) => {
    try { await api.put("/settings", { price_authority: v }); toast.success(t("set.saveOk")); onSettings?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const badge = (s) => ({ geplant: "bg-chart-3/15 text-chart-3", aktiv: "bg-primary/15 text-primary", historisch: "bg-muted text-muted-foreground" }[s]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-heading text-lg font-medium">{t("tax.title")}</h3>
          <p className="text-sm text-muted-foreground max-w-2xl">{t("tax.subtitle")}</p>
        </div>
        {isAdmin && <Button className="w-full sm:w-auto" onClick={() => setOpen(true)} data-testid="tax-new-btn"><Plus className="h-4 w-4 mr-1.5" /> {t("tax.new")}</Button>}
      </div>

      <div className="rounded-md border border-border p-3">
        <Label>{t("tax.priceAuthority")}</Label>
        <Select value={settings?.price_authority || (settings?.prices_include_tax ? "gross" : "net")} onValueChange={setAuthority} disabled={!isAdmin}>
          <SelectTrigger className="mt-1.5" data-testid="tax-price-authority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="net">{t("tax.priceAuthorityNet")}</SelectItem>
            <SelectItem value="gross">{t("tax.priceAuthorityGross")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full">
          <thead className="bg-muted/50"><tr>
            <Th>{t("tax.case")}</Th><Th right>{t("tax.rate")}</Th><Th>{t("tax.validFrom")}</Th>
            <Th>{t("tax.validTo")}</Th><Th>{t("tax.category")}</Th><Th>{t("common.status")}</Th><Th></Th></tr></thead>
          <tbody data-testid="tax-rates-table">
            {rows === null && <tr><Td colSpan={7} className="text-muted-foreground">{t("common.loading")}</Td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id} data-testid={`tax-row-${r.code}-${r.valid_from}`}>
                <Td>
                  <div className="font-medium">{t(`tax.code.${r.code}`, r.code)}</div>
                  <div className="text-xs text-muted-foreground max-w-md">{t(`tax.help.${r.code}`, "")}</div>
                </Td>
                <Td right mono>{r.rate} %</Td>
                <Td mono>{fmtDate(r.valid_from)}</Td>
                <Td mono>{r.valid_to ? fmtDate(r.valid_to) : "—"}</Td>
                <Td mono>{r.tax_category}</Td>
                <Td><span className={`rounded-full px-2 py-0.5 text-xs ${badge(r.status)}`}>{t(`tax.status.${r.status}`, r.status)}</span></Td>
                <Td right>{isAdmin && r.status === "geplant" && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(r)} data-testid={`tax-del-${r.code}-${r.valid_from}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                )}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("tax.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("tax.case")}</Label>
              <Select value={f.code} onValueChange={(v) => setF({ ...f, code: v, rate: v.startsWith("EXEMPT") || v === "ZERO_RATE" ? "0" : f.rate })}>
                <SelectTrigger className="mt-1.5" data-testid="tax-code"><SelectValue /></SelectTrigger>
                <SelectContent>{CODES.map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">{t(`tax.help.${f.code}`, "")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("tax.rate")} (%)</Label><Input type="number" step="0.1" className="mt-1.5" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="tax-rate" /></div>
              <div><Label>{t("tax.validFrom")}</Label><DatePicker value={f.valid_from} onChange={(v) => setF({ ...f, valid_from: v })} className="mt-1.5" testId="tax-valid-from" /></div>
            </div>
            {impact && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1" data-testid="tax-impact">
                <div className="font-medium">{t("tax.impact")}</div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactOld")}</span><span className="font-mono">{impact.current_rate ?? "—"} %</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactNew")}</span><span className="font-mono">{impact.new_rate} %</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.validFrom")}</span><span className="font-mono">{fmtDate(impact.valid_from)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactByCategory")}</span><span className="font-mono">{impact.variants_by_category + impact.bundles_by_category}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactByRate")}</span><span className="font-mono">{impact.variants_by_fixed_rate}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactPriceLists")}</span><span className="font-mono">{impact.price_lists}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("tax.impactScheduled")}</span><span className="font-mono">{impact.scheduled_prices_after}</span></div>
                <p className="flex gap-1.5 pt-1 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5 shrink-0" /> {t("tax.noMassUpdate")}</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} disabled={!f.valid_from || f.rate === ""} data-testid="tax-save-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
